import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Camera, ImagePlus, Plus, Trash2, Search, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { printReceipt, saveReceiptConfig } from "../lib/qz";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CustomerSearchSelect } from "../components/CustomerSearchSelect";

const TERMS = ["Cash", "Tempo"];
const MARKETPLACES = ["Shopee", "Tokopedia", "TikTok Shop", "Lazada", "Blibli", "Website", "WhatsApp", "Instagram"];

function printSale(s) {
  const rows = (s.items || []).map((it, i) =>
    `<tr><td>${i + 1}</td><td>${it.product_name || "-"}</td><td>${it.color || ""}/${it.size || ""}</td><td style="text-align:center">${it.qty}</td><td style="text-align:right">Rp ${(it.price || 0).toLocaleString("id-ID")}</td><td style="text-align:right">Rp ${(it.total || 0).toLocaleString("id-ID")}</td></tr>`).join("");
  const html = `<html><head><title>Struk ${s.number}</title><style>body{font-family:Arial;padding:24px;color:#3a2a1a;max-width:560px}h2{margin:0}table{width:100%;border-collapse:collapse;margin-top:12px}td,th{border:1px solid #ccc;padding:6px;font-size:13px}th{background:#f3ece4;text-align:left}.muted{color:#777;font-size:12px}.tot{text-align:right;margin-top:4px}</style></head><body><h2>STRUK PENJUALAN</h2><div class="muted">No: <b>${s.number}</b> &middot; ${new Date(s.created_at).toLocaleString("id-ID")}</div><div class="muted">Customer: ${s.customer_name || "-"}${s.group_name ? " &middot; Group: " + s.group_name : ""}</div>${s.keep_number ? `<div class="muted">Dari Keep: ${s.keep_number}</div>` : ""}<table><thead><tr><th>No</th><th>Produk</th><th>Varian</th><th style="text-align:center">Qty</th><th style="text-align:right">Harga</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody></table><div class="tot">Subtotal: Rp ${(s.sub_total || 0).toLocaleString("id-ID")}</div>${s.discount ? `<div class="tot">Diskon: - Rp ${(s.discount).toLocaleString("id-ID")}</div>` : ""}<div class="tot" style="font-weight:bold;font-size:15px">Grand Total: Rp ${(s.grand_total || 0).toLocaleString("id-ID")}</div><div class="tot">Bayar (${s.payment_method || "Cash"}): Rp ${(s.cash_received || 0).toLocaleString("id-ID")}</div><div class="tot">Kembalian: Rp ${(s.change || 0).toLocaleString("id-ID")}</div>${(s.sourced_from && s.sourced_from.length) ? `<div class="muted" style="margin-top:8px">Diambil dari gudang: ${s.sourced_from.map((x) => `${x.warehouse_name} (${x.qty})`).join(", ")}</div>` : ""}<p class="muted">Terima kasih telah berbelanja.</p></body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0";
  iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ }
    setTimeout(() => document.body.removeChild(iframe), 2000);
  }, 300);
}

export default function CreateSale() {
  const navigate = useNavigate();
  const location = useLocation();
  const [number, setNumber] = useState("—");
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selectedKeep, setSelectedKeep] = useState(null);
  const [form, setForm] = useState({ group_id: "", customer_id: "", term: "Cash", note: "", marketplace: "", order_id: "" });
  const [selCust, setSelCust] = useState(null);
  const [rows, setRows] = useState([]);
  const [discType, setDiscType] = useState("none");
  const [discValue, setDiscValue] = useState("");
  const [showShipping, setShowShipping] = useState(false);
  const [shippingCost, setShippingCost] = useState("");
  const [payMethod, setPayMethod] = useState("Cash");
  const [cash, setCash] = useState("");
  const [pickOpen, setPickOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasProof, setHasProof] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const cameraRef = useRef();
  const galleryRef = useRef();

  const isKeep = !!selectedKeep;
  const selGroup = groups.find((g) => g.id === form.group_id);
  const grpNameSel = isKeep ? (selectedKeep?.group_name || "") : (selGroup?.name || "");
  const isOnline = grpNameSel.toLowerCase() === "online";
  const isMktGroup = !isKeep && !!selGroup?.marketplace;
  const showMkt = !isKeep && (isOnline || isMktGroup);
  const isBankTransfer = /bank|transfer|bca|bri|bni|mandiri/i.test(payMethod || "");

  useEffect(() => {
    api.get("/cashier/next-number").then((r) => setNumber(r.data.number)).catch(() => setNumber("SALES—"));
    api.get("/settings/receipt").then((r) => saveReceiptConfig(r.data)).catch(() => {});
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/payment-methods", { params: { active: "active" } }).then((r) => {
      setMethods(r.data); if (r.data[0]) setPayMethod(r.data[0].name);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const draft = location.state?.draft;
    if (!draft) return;
    setForm((f) => ({
      ...f,
      group_id: draft.group_id || f.group_id,
      customer_id: draft.customer_id || "",
      marketplace: draft.marketplace || "",
      order_id: draft.order_id || "",
    }));
    setRows((draft.items || []).map((it) => ({
      variant_id: it.variant_id, product_name: it.product_name, color: it.color,
      size: it.size, sku: it.sku, price: it.price, qty: it.qty,
    })));
    toast.success("Draft kasir dari Order Marketplace dimuat — periksa harga & simpan");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/customers", { params: { active: "active", group_id: form.group_id || undefined } }).then((r) => setCustomers(r.data)).catch(() => {});
  }, [form.group_id]);

  const subtotal = useMemo(() => rows.reduce((s, r) => s + (parseInt(r.price) || 0) * (parseInt(r.qty) || 0), 0), [rows]);
  const discountAmt = useMemo(() => {
    const v = parseInt(discValue) || 0;
    if (discType === "percent") return Math.min(Math.round(subtotal * Math.min(v, 100) / 100), subtotal);
    if (discType === "amount") return Math.min(v, subtotal);
    return 0;
  }, [discType, discValue, subtotal]);
  const shippingAmount = Math.max(parseInt(shippingCost) || 0, 0);
  const grandTotal = subtotal - discountAmt + shippingAmount;
  const change = Math.max((parseInt(cash) || 0) - grandTotal, 0);

  useEffect(() => {
    if (!isBankTransfer) {
      setHasProof(false);
      setProofFile(null);
    }
  }, [isBankTransfer]);

  const applyKeep = (k) => {
    setSelectedKeep(k);
    setForm((f) => ({ ...f, group_id: k.group_id || "", customer_id: k.customer_id || "" }));
    setRows((k.items || []).map((it) => ({
      variant_id: it.variant_id, product_name: it.product_name, color: it.color, size: it.size,
      warehouse_id: it.warehouse_id, warehouse_name: it.warehouse_name, keep_item_id: it.keep_item_id,
      price: it.price, qty: it.qty_remaining, max: it.qty_remaining,
    })));
    toast.success(`Keep ${k.number} dimuat`);
  };
  const clearKeep = () => { setSelectedKeep(null); setRows([]); setSelCust(null); setForm({ group_id: "", customer_id: "", term: form.term, note: form.note }); };

  const selectBandung = async () => {
    let list = groups;
    let g = list.find((x) => x.code === "SHP-BDG" || (x.marketplace === "Shopee" && (x.name || "").toLowerCase() === "shopee bandung"));
    if (!g) {
      try {
        const r = await api.post("/customer-groups/seed-shopee-bandung");
        const rg = await api.get("/customer-groups", { params: { active: "active" } });
        setGroups(rg.data);
        g = rg.data.find((x) => x.id === r.data.group_id || x.code === "SHP-BDG");
        if (!r.data.warehouse_linked) toast("Group Shopee Bandung dibuat, tapi warehouse 'Gudang Bandung' (GDG-BANDUNG) belum ada. Buat warehouse-nya lalu klik lagi agar stok terkunci ke Bandung.", { duration: 8000 });
      } catch (e) { toast.error("Gagal menyiapkan group Shopee Bandung"); return; }
    }
    if (!g) { toast.error("Group Shopee Bandung tidak ditemukan"); return; }
    setSelectedKeep(null); setRows([]); setSelCust(null);
    setForm((f) => ({ ...f, group_id: g.id, customer_id: "", marketplace: "Shopee" }));
    toast.success(g.default_warehouse_id ? "Preset Kasir Bandung aktif — stok dikunci ke Gudang Bandung" : "Preset Kasir Bandung aktif (warehouse Bandung belum ditautkan)");
  };

  const addProduct = (v) => {
    if (rows.some((r) => r.variant_id === v.variant_id && r.warehouse_id === v.warehouse_id)) { toast.error("Varian dari gudang ini sudah ditambahkan"); return; }
    setRows((rs) => [...rs, { ...v, qty: 1 }]);
    toast.success(`Ditambahkan dari ${v.warehouse_name}`);
  };
  const setRowQty = (v, qty) => setRows((rs) => {
    const idx = rs.findIndex((r) => r.variant_id === v.variant_id && !r.keep_item_id);
    if (qty <= 0) return rs.filter((r) => !(r.variant_id === v.variant_id && !r.keep_item_id));
    if (idx === -1) return [...rs, { ...v, qty }];
    const n = [...rs]; n[idx] = { ...n[idx], qty }; return n;
  });
  const setRow = (i, patch) => { const n = [...rows]; n[i] = { ...n[i], ...patch }; setRows(n); };
  const reset = () => {
    clearKeep();
    setForm({ group_id: "", customer_id: "", term: "Cash", note: "" });
    setDiscType("none");
    setDiscValue("");
    setShowShipping(false);
    setShippingCost("");
    setCash("");
    setHasProof(false);
    setProofFile(null);
  };

  const save = async () => {
    if (saving) return;
    const items = rows.filter((r) => r.variant_id && parseInt(r.qty) > 0).map((r) => ({
      variant_id: r.variant_id, warehouse_id: r.warehouse_id, qty: parseInt(r.qty) || 0,
      price: parseInt(r.price) || 0, keep_item_id: r.keep_item_id || null,
    }));
    if (!items.length) return toast.error("Tambahkan minimal satu produk");
    if (isKeep && rows.some((r) => (parseInt(r.qty) || 0) > (r.max || 0))) return toast.error("Qty melebihi sisa Keep");
    if (isBankTransfer && hasProof && !proofFile) {
      return toast.error("Pilih foto bukti transfer terlebih dahulu");
    }
    const cust = selCust || customers.find((c) => c.id === form.customer_id);
    const grp = groups.find((g) => g.id === form.group_id);
    const payload = {
      mode: isKeep ? "keep" : "direct", keep_id: isKeep ? selectedKeep.keep_id : null,
      customer_id: form.customer_id || (isKeep ? selectedKeep.customer_id : null),
      customer_name: cust?.name || (isKeep ? selectedKeep.customer_name : ""),
      group_id: isKeep ? selectedKeep.group_id : (form.group_id || null),
      group_name: isKeep ? selectedKeep.group_name : (grp?.name || ""),
      channel: "Kasir", term_of_payment: form.term, note: form.note, items,
      marketplace: showMkt ? form.marketplace : "", order_id: showMkt ? form.order_id : "",
      tracking_no: form.tracking_no || "",
      discount_type: discType, discount_value: parseInt(discValue) || 0,
      payment_method: payMethod, cash_received: parseInt(cash) || 0,
      shipping_cost: shippingAmount,
      idempotency_key: `SALE-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setSaving(true);
    try {
      const r = await api.post("/cashier/sale", payload);
      if (hasProof && proofFile) {
        const upload = new FormData();
        upload.append("file", proofFile);
        try {
          await api.post(`/cashier/sale/${r.data.id}/transfer-proof`, upload);
        } catch (uploadError) {
          toast.error(
            "Penjualan tersimpan, tetapi bukti belum terunggah. Tambahkan dari Detail Sales."
          );
        }
      }
      toast.success(`Penjualan ${r.data.number} tersimpan`);
      const sf = r.data.sourced_from || [];
      if (sf.length > 1 || sf.some((s) => s.warehouse_name !== (r.data.warehouse_name || ""))) {
        toast.info(`Stok diambil dari: ${sf.map((s) => `${s.warehouse_name} (${s.qty})`).join(", ")}`, { duration: 6000 });
      }
      printReceipt(r.data);
      navigate("/sales");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  const custName = isKeep ? selectedKeep.customer_name : (customers.find((c) => c.id === form.customer_id)?.name || "");
  const grpName = isKeep ? selectedKeep.group_name : (groups.find((g) => g.id === form.group_id)?.name || "");

  return (
    <div data-testid="create-sale-page">
      <PageHeader title="Create Sale" subtitle="Buat penjualan dari Keep Code atau produk langsung" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={selectBandung} data-testid="preset-cashier-bandung" className="border-cocoa text-cocoa">Kasir Bandung (Shopee)</Button>
        {isMktGroup && <span className="text-xs font-medium text-emerald-700" data-testid="mkt-group-lock-note">Gudang dikunci ke gudang default group "{grpNameSel}" ({selGroup?.default_warehouse_name || "belum ditautkan"})</span>}
      </div>

      <Panel className="p-5 mb-4">
        <div className="text-sm text-cocoa mb-4">NO SALE : <span className="font-mono font-bold text-status-passed" data-testid="sale-number">{number}</span></div>

        {/* Keep Code search */}
        <div className="mb-4">
          <Label>Keep Code</Label>
          <KeepCodeSearch selectedKeep={selectedKeep} onSelect={applyKeep} onClear={clearKeep} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Group</Label>
            {isKeep ? (
              <Input value={grpName || "-"} disabled className="bg-taupe/10" data-testid="sale-group-readonly" />
            ) : (
              <Select value={form.group_id || undefined} onValueChange={(v) => { const g = groups.find((x) => x.id === v); setForm({ ...form, group_id: v, customer_id: "", marketplace: g?.marketplace || form.marketplace }); setSelCust(null); }}>
                <SelectTrigger className="bg-white" data-testid="sale-group-select"><SelectValue placeholder="Select Group" /></SelectTrigger>
                <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label>Customer</Label>
            {isKeep ? (
              <Input value={custName || "-"} disabled className="bg-taupe/10" data-testid="sale-customer-readonly" />
            ) : (
              <CustomerSearchSelect groupId={form.group_id} selected={selCust} testid="sale-customer-search"
                onSelect={(c) => { setSelCust(c); setForm((f) => ({ ...f, customer_id: c.id, marketplace: (isOnline && c.marketplace) ? c.marketplace : f.marketplace })); }}
                onClear={() => { setSelCust(null); setForm((f) => ({ ...f, customer_id: "" })); }} />
            )}
          </div>
          <div>
            <Label>Term of Payment</Label>
            <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
              <SelectTrigger className="bg-white" data-testid="sale-term-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {showMkt && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="sale-marketplace-fields">
            <div><Label>Marketplace</Label>
              <Select value={form.marketplace || undefined} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
                <SelectTrigger className="bg-white" data-testid="sale-marketplace-select"><SelectValue placeholder="Select Marketplace" /></SelectTrigger>
                <SelectContent>{MARKETPLACES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Order ID Marketplace</Label><Input value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="Enter Order ID" className="bg-white" data-testid="sale-order-id-input" /></div>
          </div>
        )}
        <div className="mt-4 max-w-md">
          <Label>No. Resi Pengiriman (opsional)</Label>
          <Input value={form.tracking_no || ""} onChange={(e) => setForm({ ...form, tracking_no: e.target.value })} placeholder="mis. JX1234567890 (bisa diisi belakangan dari daftar Sales)" className="bg-white" data-testid="sale-tracking-input" />
        </div>
      </Panel>

      <Panel className="mb-4">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead><TableHead>Product Name</TableHead>
              <TableHead>Color</TableHead><TableHead>Size</TableHead>
              <TableHead className="text-right">Price (@)</TableHead>
              <TableHead className="text-center w-24">Qty</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.variant_id}-${r.warehouse_id}`} data-testid={`sale-row-${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{r.product_name}<div className="text-[11px] text-wmuted">{r.warehouse_name}{r.max != null ? ` · sisa ${r.max}` : ""}</div></TableCell>
                <TableCell>{r.color}</TableCell>
                <TableCell>{r.size}</TableCell>
                <TableCell className="text-right"><Input type="number" className="w-28 text-right ml-auto" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`sale-row-price-${i}`} /></TableCell>
                <TableCell className="text-center"><Input type="number" className="w-20 text-center mx-auto" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`sale-row-qty-${i}`} /></TableCell>
                <TableCell className="text-right font-medium">{fmtIDR((parseInt(r.price) || 0) * (parseInt(r.qty) || 0))}</TableCell>
                <TableCell>{!isKeep && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`sale-row-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-8">{isKeep ? "Keep tanpa sisa item." : "Belum ada produk."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        {!isKeep && (
          <div className="flex items-center justify-center py-4 border-t border-taupe/15">
            <Button onClick={() => setPickOpen(true)} data-testid="add-product-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> Add Product</Button>
          </div>
        )}
      </Panel>

      <Panel className="p-5 mb-4">
        <Label>Sale Note</Label>
        <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} data-testid="sale-note-input" className="bg-white" />
      </Panel>

      <Panel className="p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label>Diskon</Label>
            <div className="flex gap-2">
              <Select value={discType} onValueChange={setDiscType}>
                <SelectTrigger className="bg-white w-32" data-testid="disc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa</SelectItem>
                  <SelectItem value="amount">Rp</SelectItem>
                  <SelectItem value="percent">%</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" value={discValue} onChange={(e) => setDiscValue(e.target.value)} disabled={discType === "none"} className="bg-white" placeholder="0" data-testid="disc-value-input" />
            </div>
          </div>
          <div>
            <Label>Ongkos Kirim</Label>
            {showShipping ? (
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  placeholder="Rp 0"
                  className="bg-white"
                  data-testid="sale-shipping-cost-input"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    setShowShipping(false);
                    setShippingCost("");
                  }}
                  data-testid="sale-shipping-remove-button"
                  title="Hapus ongkos kirim"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="border-taupe text-cocoa"
                onClick={() => setShowShipping(true)}
                data-testid="sale-add-shipping-button"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Tambah Ongkir
              </Button>
            )}
          </div>
          <div className="md:col-span-2 flex items-end justify-end">
            <div className="text-sm space-y-1 w-full max-w-xs">
              <div className="flex justify-between"><span className="text-wmuted">Subtotal</span><span data-testid="sale-subtotal">{fmtIDR(subtotal)}</span></div>
              {discountAmt ? <div className="flex justify-between text-status-reject"><span>Diskon</span><span data-testid="sale-discount">- {fmtIDR(discountAmt)}</span></div> : null}
              {shippingAmount ? <div className="flex justify-between"><span>Ongkos Kirim</span><span data-testid="sale-shipping-cost">{fmtIDR(shippingAmount)}</span></div> : null}
              <div className="flex justify-between font-bold text-cocoa text-base pt-1 border-t border-taupe/20"><span>Grand Total</span><span data-testid="sale-grandtotal">{fmtIDR(grandTotal)}</span></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Payment Type</Label>
            <Select value={payMethod || undefined} onValueChange={setPayMethod}>
              <SelectTrigger className="bg-white" data-testid="pay-method-select"><SelectValue placeholder="Pilih metode" /></SelectTrigger>
              <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cash Received</Label>
            <Input type="number" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="Rp." className="bg-white" data-testid="cash-received-input" />
          </div>
          <div>
            <Label>Change</Label>
            <Input value={fmtIDR(change)} disabled className="bg-taupe/10 font-semibold text-status-passed" data-testid="sale-change" />
          </div>
        </div>
        {isBankTransfer && (
          <div className="mt-4 border-t border-taupe/15 pt-3" data-testid="transfer-proof-section">
            <label className="flex items-center gap-2 text-sm font-medium text-cocoa">
              <input type="checkbox" checked={hasProof} onChange={(e) => { setHasProof(e.target.checked); if (!e.target.checked) setProofFile(null); }} data-testid="transfer-proof-checkbox" />
              Ada Bukti Transfer
            </label>
            {hasProof && <div className="mt-2 flex flex-wrap items-center gap-2">
              <input ref={cameraRef} className="hidden" accept="image/jpeg,image/png,image/webp" capture="environment" type="file" onChange={(e) => setProofFile(e.target.files?.[0] || null)} data-testid="transfer-proof-camera-input" />
              <input ref={galleryRef} className="hidden" accept="image/jpeg,image/png,image/webp" type="file" onChange={(e) => setProofFile(e.target.files?.[0] || null)} data-testid="transfer-proof-gallery-input" />
              <Button type="button" size="sm" variant="outline" onClick={() => cameraRef.current?.click()} data-testid="transfer-proof-camera-btn"><Camera className="w-4 h-4 mr-1" />Kamera</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => galleryRef.current?.click()} data-testid="transfer-proof-gallery-btn"><ImagePlus className="w-4 h-4 mr-1" />Galeri</Button>
              <span className="text-xs text-wmuted" data-testid="transfer-proof-filename">{proofFile?.name || "Belum ada file"}</span>
            </div>}
          </div>
        )}
      </Panel>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={reset} className="border-status-reject/40 text-status-reject" data-testid="sale-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
        <Button onClick={save} disabled={saving || !rows.length} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="save-sale-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : "Save"}</Button>
      </div>

      <ProductPicker open={pickOpen} setOpen={setPickOpen} rows={rows} onSetQty={setRowQty} groupId={isKeep ? selectedKeep?.group_id : form.group_id} />
    </div>
  );
}

function ProductPicker({ open, setOpen, rows, onSetQty, groupId }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async (term) => {
    setLoading(true);
    try {
      const r = await api.get("/cashier/recent-products", { params: { q: term || undefined, group_id: groupId || undefined } });
      setProducts((r.data.products || []).map((p) => ({ ...p, variants: (p.variants || []).map((v) => ({ ...v, total: (v.warehouses || []).reduce((s, w) => s + (w.available || 0), 0) })) })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setQ(""); doSearch(""); } /* eslint-disable-next-line */ }, [open]);
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length === 0) { doSearch(""); return; }
    if (term.length < 2) { setProducts([]); return; }
    const t = setTimeout(() => doSearch(term), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, open]);
  const qtyOf = (vid) => rows.find((r) => r.variant_id === vid && !r.keep_item_id)?.qty || 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Add Product</DialogTitle></DialogHeader>
        <div className="mb-3">
          <Label className="text-xs">Product</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik nama produk / SKU (otomatis)..." className="pl-9 bg-white" data-testid="sale-product-search" autoFocus />
            {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-wmuted">Mencari...</span>}
          </div>
          <p className="text-xs text-wmuted mt-1">{q.trim().length === 0 ? "Menampilkan produk yang sering dijual — atau ketik untuk mencari." : "Total Stock = stok gabungan semua gudang (referensi). Alokasi aktual mengikuti routing bucket."}</p>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Color</TableHead><TableHead>Size</TableHead><TableHead className="text-center">Total Stock</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-center w-44">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <React.Fragment key={p.product_id}>
                <TableRow className="hover:bg-transparent bg-taupe/10"><TableCell colSpan={5} className="font-semibold text-cocoa py-2" data-testid={`sale-pick-product-${p.product_id}`}><div className="flex items-center gap-2">{p.product_name}{p.keep_as_po && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cocoa/10 text-cocoa font-semibold" data-testid={`sale-pick-po-${p.product_id}`}>Produk PO</span>}{p.keep_as_po && <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] border-cocoa/30 text-cocoa ml-auto" onClick={() => { setOpen(false); navigate("/sales/preorder-keep-create"); }} data-testid={`sale-pick-makepo-${p.product_id}`}>Buat Keep PO</Button>}</div></TableCell></TableRow>
                {p.variants.map((v) => {
                  const cur = qtyOf(v.variant_id);
                  const vobj = { variant_id: v.variant_id, product_id: p.product_id, product_name: p.product_name, color: v.color, size: v.size, price: v.price, available: v.total };
                  return (
                    <TableRow key={v.variant_id} data-testid={`sale-pick-row-${v.variant_id}`}>
                      <TableCell className="text-sm">{v.color}{v.borrowed_flag ? <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`sale-borrowed-${v.variant_id}`}>{v.borrowed_flag} dipinjam</span> : null}</TableCell>
                      <TableCell className="text-sm">{v.size}</TableCell>
                      <TableCell className="text-center font-medium">{v.total <= 0 ? <span className="text-xs font-semibold text-status-reject" data-testid={`sale-pick-empty-${v.variant_id}`}>Habis (0)</span> : v.total}{v.po_ready > 0 && <div className="text-[10px] font-semibold text-amber-600" data-testid={`sale-pick-poready-${v.variant_id}`}>Stok PO: {v.po_ready}</div>}</TableCell>
                      <TableCell className="text-right text-sm">{fmtIDR(v.price)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" disabled={cur <= 0} onClick={() => onSetQty(vobj, Math.max(0, cur - 1))} data-testid={`sale-pick-minus-${v.variant_id}`}>−</Button>
                          <Input type="number" className="w-16 h-8 text-center" value={cur || ""} disabled={v.total <= 0} onChange={(e) => onSetQty(vobj, Math.min(v.total, Math.max(0, parseInt(e.target.value) || 0)))} data-testid={`sale-pick-qty-${v.variant_id}`} />
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" disabled={v.total <= 0 || cur >= v.total} onClick={() => onSetQty(vobj, Math.min(cur + 1, v.total))} data-testid={`sale-pick-plus-${v.variant_id}`}>+</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            ))}
            {!products.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">{loading ? "Mencari..." : "Ketik minimal 2 huruf, hasil tampil otomatis."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <DialogFooter><Button onClick={() => setOpen(false)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="sale-pick-done-btn">Selesai</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeepCodeSearch({ selectedKeep, onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (selectedKeep) return;
    const term = q.trim();
    if (term.length < 1) { setItems([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await api.get("/cashier/keep-search", { params: { q: term } }); setItems(r.data.items || []); setOpen(true); }
      catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, selectedKeep]);

  if (selectedKeep) {
    return (
      <div className="flex items-center gap-2 bg-status-passed/10 border border-status-passed/30 rounded-md px-3 py-2" data-testid="keep-selected-box">
        <span className="font-mono font-bold text-cocoa" data-testid="selected-keep-number">{selectedKeep.number}</span>
        <span className="text-sm text-wmuted">· {selectedKeep.customer_name || "-"}{selectedKeep.group_name ? ` · ${selectedKeep.group_name}` : ""} · {selectedKeep.items.length} item</span>
        <Button size="icon" variant="ghost" onClick={onClear} className="ml-auto h-7 w-7" data-testid="keep-clear-btn"><X className="w-4 h-4 text-status-reject" /></Button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => items.length && setOpen(true)}
        placeholder="Search by Keep Code or Order ID" className="pl-9 bg-white" data-testid="keep-code-input" />
      {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-wmuted">...</span>}
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-taupe/30 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="keep-suggestions">
          {items.map((k) => (
            <button key={k.keep_id} onClick={() => { onSelect(k); setOpen(false); setQ(""); }} data-testid={`keep-suggest-${k.keep_id}`}
              className="w-full text-left px-3 py-2 hover:bg-taupe/10 border-b border-taupe/10 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-cocoa">{k.number}</span>
                <span className="text-[10px] bg-taupe/20 px-1.5 py-0.5 rounded">{k.status}</span>
              </div>
              <div className="text-xs text-wmuted">{k.customer_name || "-"}{k.group_name ? ` · ${k.group_name}` : ""} · {k.items.length} item</div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && q.trim() && !items.length && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-taupe/30 rounded-md shadow-lg px-3 py-2 text-sm text-wmuted">Keep tidak ditemukan.</div>
      )}
    </div>
  );
}
